import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import { KbRagPage } from "./pages/KbRagPage";

export type KbRagTab =
  | "overview"
  | "articles"
  | "runs"
  | "search"
  | "ask"
  | "review"
  | "impact"
  | "how-to";

export type ReviewFilter = "new" | "accepted" | "rejected" | "done" | "all";

export interface KbRagSearch {
  tab?: KbRagTab;
  /** Category filter, shared by Articles, Search and Ask. */
  cat?: string;
  /** Last search query, so a search survives a reload and can be linked to. */
  q?: string;
  /** Article open in the drawer (document_id). */
  article?: string;
  /** Article Review desk: the day being worked (YYYY-MM-DD). */
  day?: string;
  /** Article Review desk: which review states to show. Defaults to "new". */
  rs?: ReviewFilter;
  /** Release Impact: the scan being viewed (scan_id). */
  scan?: string;
}

const TABS: readonly KbRagTab[] = [
  "overview",
  "articles",
  "runs",
  "search",
  "ask",
  "review",
  "impact",
  "how-to",
];

const REVIEW_FILTERS: readonly ReviewFilter[] = [
  "new",
  "accepted",
  "rejected",
  "done",
  "all",
];

function validateKbRagSearch(search: Record<string, unknown>): KbRagSearch {
  const out: KbRagSearch = {};
  if (TABS.includes(search.tab as KbRagTab)) out.tab = search.tab as KbRagTab;
  if (typeof search.cat === "string" && search.cat) out.cat = search.cat;
  if (typeof search.q === "string" && search.q) out.q = search.q;
  if (typeof search.article === "string" && search.article)
    out.article = search.article;
  if (
    typeof search.day === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(search.day)
  ) {
    out.day = search.day;
  }
  if (REVIEW_FILTERS.includes(search.rs as ReviewFilter))
    out.rs = search.rs as ReviewFilter;
  if (typeof search.scan === "string" && search.scan) out.scan = search.scan;
  return out;
}

/**
 * KB RAG — the Cinch support knowledge base (support.cinch.io) as the
 * AI agent sees it: a nightly crawl into a Qdrant index, monitored here.
 *
 * Sits next to Code RAG in Monitors and mirrors its shape (Overview / Search /
 * Ask), plus what a knowledge base needs that code does not: an article table
 * with per-article sync status, the run history with its login-failure alarm,
 * and the two reviewer views — the daily Article Review desk and the Thursday
 * Release Impact scan — that Sarah Paxman works from.
 *
 * Unlike code_rag this is an ordinary Monitors feature: the articles are
 * documentation every staff member can already open, so a "Monitors" category
 * grant may confer `kb_rag.read`. `kb_rag.write` is the review desk.
 */
export const kbRagModule: FeatureModule = {
  key: "kb_rag",
  nav: {
    label: "KB RAG",
    icon: BookOpen,
    to: "/kb-rag",
    requires: "kb_rag.read",
    order: 42, // directly after Code RAG (41)
    group: "Monitors",
  },
  routes: (rootRoute) =>
    [
      createRoute({
        getParentRoute: () => rootRoute,
        path: "/kb-rag",
        component: guarded("kb_rag.read", KbRagPage),
        validateSearch: validateKbRagSearch,
      }),
    ] as unknown as AnyRoute[],
};
